import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Dropdown, Spin } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import { PersonPhoto } from '@/components/person-photo';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 3 * 1024 * 1024;
const SIZE = 44;

const initials = (first = '', last = '') =>
  `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase();

/**
 * Round avatar in the profile header: the photo when there is one, initials
 * otherwise. HR of the company (and HLB) can add, replace or remove it from
 * here; the same picture is the linked account's avatar.
 */
export default function EmployeeProfilePhoto({ employee, employeeId, canEdit }) {
  const { t } = useTranslation(['translations', 'messages']);
  const tp = (key) => t(`employeeProfile.${key}`, { ns: 'translations' });
  const notify = useNotify();
  const { modal } = App.useApp();
  const queryClient = useQueryClient();
  const inputRef = useRef(null);

  const photoKey = employee?.photo?.key || null;
  const fullName = [employee?.firstName, employee?.lastName]
    .filter(Boolean)
    .join(' ');

  const onDone = (response) => {
    notify.success({
      message: t('success', { ns: 'messages' }),
      description: response?.message,
    });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.employee, employeeId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.employees] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.sectorStructure] });
    // The linked account shows the same picture (header avatar, user form).
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.user] });
  };
  const onFail = (error) =>
    notify.error({
      message: t('error', { ns: 'messages' }),
      description: error?.response?.data?.message || error?.message,
    });

  const upload = useMutation({
    mutationFn: (file) => API.employees.uploadEmployeePhoto(employeeId, file),
    onSuccess: onDone,
    onError: onFail,
  });
  const remove = useMutation({
    mutationFn: () => API.employees.removeEmployeePhoto(employeeId),
    onSuccess: onDone,
    onError: onFail,
  });
  const busy = upload.isPending || remove.isPending;

  const onFileChosen = (event) => {
    const file = event.target.files?.[0];
    // Same file picked again must fire onChange next time too.
    event.target.value = '';
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      notify.error({
        message: t('error', { ns: 'messages' }),
        description: tp('photoInvalidType'),
      });
      return;
    }
    if (file.size > MAX_BYTES) {
      notify.error({
        message: t('error', { ns: 'messages' }),
        description: tp('photoTooLarge'),
      });
      return;
    }
    upload.mutate(file);
  };

  const avatar = (
    <PersonPhoto
      photoKey={photoKey}
      size={SIZE}
      alt={fullName}
      className="border-2 border-white"
      fallback={
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white font-extrabold text-accent">
          {initials(employee?.firstName, employee?.lastName)}
        </div>
      }
    />
  );

  if (!canEdit) return <div className="shrink-0">{avatar}</div>;

  const items = [
    {
      key: 'upload',
      label: photoKey ? tp('photoChange') : tp('photoUpload'),
      extra: <span className="text-xs text-zinc-400">{tp('photoHint')}</span>,
      onClick: () => inputRef.current?.click(),
    },
    photoKey && {
      key: 'remove',
      danger: true,
      label: tp('photoRemove'),
      onClick: () =>
        modal.confirm({
          title: tp('photoRemoveConfirm'),
          okText: tp('photoRemove'),
          okButtonProps: { danger: true, shape: 'round' },
          cancelButtonProps: { shape: 'round' },
          onOk: () => remove.mutateAsync(),
        }),
    },
  ].filter(Boolean);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={onFileChosen}
      />
      <Dropdown menu={{ items }} trigger={['click']} disabled={busy}>
        <button
          type="button"
          aria-label={photoKey ? tp('photoChange') : tp('photoUpload')}
          title={photoKey ? tp('photoChange') : tp('photoUpload')}
          className="group relative shrink-0 cursor-pointer rounded-full border-0 bg-transparent p-0"
        >
          {avatar}
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            <CameraOutlined />
          </span>
          {busy && (
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
              <Spin size="small" />
            </span>
          )}
        </button>
      </Dropdown>
    </>
  );
}
